// ---------------------------------------------------------------------------
// CallBooking Renderer (editor key: CallBooking)
// ---------------------------------------------------------------------------
// Renders a compact "schedule a call" card for sales / consult pages. The
// primary CTA URL routes through the URL safelist; without a safe URL the
// renderer emits an inert `<button>` so the markup is keyboard reachable
// without fabricating a destination. The optional phone number is stripped
// of non-dial characters before becoming a `tel:` link; numbers with fewer
// than five dialable digits render as plain text. The duration label is
// treated as display text only.

import { registerRenderer } from "../registry.js";
import type { RenderContext } from "../types.js";
import { attr, text, url } from "../escape.js";

interface CallBookingProps {
  heading?: string;
  description?: string;
  duration?: string;
  buttonLabel?: string;
  buttonUrl?: string;
  phone?: string;
  phoneLabel?: string;
}

const DEFAULT_HEADING = "Schedule a Call";
const DEFAULT_BUTTON_LABEL = "Pick a Time";
const DEFAULT_PHONE_LABEL = "Or call directly";

const DIALABLE_PATTERN = /[^0-9+]/g;
const MIN_DIALABLE_DIGITS = 5;

function nonEmpty(value: unknown, fallback: string): string {
  if (typeof value !== "string") return fallback;
  const trimmed = value.trim();
  return trimmed ? trimmed : fallback;
}

function buildTelHref(phone: string): string {
  const cleaned = phone.replace(DIALABLE_PATTERN, "");
  // Strip stray "+" characters except for an optional leading one.
  const normalized = cleaned.startsWith("+")
    ? `+${cleaned.slice(1).replace(/\+/g, "")}`
    : cleaned.replace(/\+/g, "");
  const digitCount = normalized.replace(/[^0-9]/g, "").length;
  if (digitCount < MIN_DIALABLE_DIGITS) return "";
  return `tel:${normalized}`;
}

function render(props: Record<string, unknown>, _ctx: RenderContext): string {
  const p = props as CallBookingProps;

  const heading = nonEmpty(p.heading, DEFAULT_HEADING);
  const description = typeof p.description === "string" ? p.description.trim() : "";
  const duration = typeof p.duration === "string" ? p.duration.trim() : "";
  const buttonLabel = nonEmpty(p.buttonLabel, DEFAULT_BUTTON_LABEL);
  const phoneLabel = nonEmpty(p.phoneLabel, DEFAULT_PHONE_LABEL);
  const phone = typeof p.phone === "string" ? p.phone.trim() : "";
  const rawUrl = typeof p.buttonUrl === "string" ? p.buttonUrl.trim() : "";
  const sanitizedUrl = rawUrl ? url(rawUrl) : "";
  const hasSafeUrl = sanitizedUrl.length > 0 && sanitizedUrl !== "#";

  const descriptionHtml = description
    ? `\n      <p class="ease-call-booking__description">${text(description)}</p>`
    : "";
  const durationHtml = duration
    ? `\n      <p class="ease-call-booking__duration" data-ease-duration="${attr(duration)}">${text(duration)} call</p>`
    : "";

  const ctaHtml = hasSafeUrl
    ? `<a class="ease-call-booking__cta ease-btn ease-btn-primary" href="${sanitizedUrl}">${text(buttonLabel)}</a>`
    : `<button class="ease-call-booking__cta ease-btn ease-btn-primary" type="button">${text(buttonLabel)}</button>`;

  let phoneHtml = "";
  if (phone) {
    const telHref = buildTelHref(phone);
    phoneHtml = telHref
      ? `\n      <p class="ease-call-booking__phone">${text(phoneLabel)}: <a class="ease-call-booking__phone-link" href="${attr(telHref)}">${text(phone)}</a></p>`
      : `\n      <p class="ease-call-booking__phone">${text(phoneLabel)}: <span class="ease-call-booking__phone-text">${text(phone)}</span></p>`;
  }

  return `<section class="ease-section ease-call-booking">
  <div class="ease-call-booking__inner">
    <article class="ease-call-booking__card">
      <h2 class="ease-call-booking__heading">${text(heading)}</h2>${descriptionHtml}${durationHtml}
      ${ctaHtml}${phoneHtml}
    </article>
  </div>
</section>`;
}

registerRenderer("call-booking", { render });
